/**
 * Session expiry detection (ALL-5718, IAM-134, specs/session-expiry-redirect).
 *
 * A 401 on its own does not prove the browser session ended: on dev an expired
 * service key behind the gateway also answers 401 (AUTH_EXPIRED_CREDENTIAL), and
 * the gateway has been seen to answer a transient 401. Redirecting on every 401
 * would throw people out to sign-in in a loop. So a 401 only *raises the
 * question*; `GET /auth/session` answers it:
 *
 *   - 401, or 2xx whose body is not `authenticated: true`  -> the session ended
 *   - 2xx with `authenticated: true`                       -> not an end (the page keeps its own error)
 *   - network error, 5xx or any other status               -> not an end (we cannot tell; fail safe)
 *
 * Concurrent 401s share one probe (single flight) and produce one end. The end
 * is reported through one registered callback, so this module needs neither
 * React, the router nor the store and is testable on its own.
 *
 * Nothing here changes an auth control: no TTL, cookie, CSRF or IdP behaviour.
 * The only stored value is the loop-guard timestamp (no identity, no path).
 */
import { isSafeReturnPath } from "@/lib/step-up-resume";

export const SESSION_PROBE_PATH = "/auth/session";
export const SESSION_ENDED_GUARD_KEY = "alloy.session-ended.last";
/** Seconds the session-ended state waits before it opens sign-in on its own. */
export const SESSION_ENDED_REDIRECT_SECONDS = 5;
/** At most one automatic redirect per this window per tab (loop guard). */
export const SESSION_ENDED_LOOP_GUARD_MS = 30_000;

export interface SessionEndedEvent {
  /** Validated same-origin path to come back to after sign-in ("/" when unsafe). */
  returnTo: string;
  /** False inside the loop-guard window: the state then waits for the button. */
  autoRedirect: boolean;
}

interface SessionExpiryConfig {
  /** Only a session that was established can end; false on the sign-in pages. */
  isArmed: () => boolean;
  onEnded: (event: SessionEndedEvent) => void;
  fetchImpl?: typeof fetch;
  now?: () => number;
  location?: () => { pathname: string; search: string; hash: string };
}

let config: SessionExpiryConfig | null = null;
let inFlight: Promise<boolean> | null = null;
let ended = false;

/** Registers the shell's handler. Returns an unregister function. */
export function configureSessionExpiry(next: SessionExpiryConfig): () => void {
  config = next;
  return () => {
    if (config === next) config = null;
  };
}

/** Test helper: forget every module-level state. */
export function resetSessionExpiryForTests(): void {
  config = null;
  inFlight = null;
  ended = false;
}

/** The auth flow's own routes never raise the question (the probe itself, login, callback, logout). */
export function isSessionProbeExempt(path: string): boolean {
  let pathname = path;
  try {
    pathname = new URL(path, "http://relative.invalid").pathname;
  } catch {
    return true;
  }
  return pathname === "/auth" || pathname.startsWith("/auth/");
}

/** A return path the gateway will accept, or "/". */
export function safeReturnPath(value: unknown): string {
  return isSafeReturnPath(value) ? value : "/";
}

/** Where the person is now, as a return path. The sign-in pages themselves return "/". */
export function currentReturnPath(loc?: { pathname: string; search: string; hash: string }): string {
  const where = loc ?? (typeof window !== "undefined" ? window.location : undefined);
  if (!where) return "/";
  if (["/login", "/mfa", "/session-expired", "/error"].includes(where.pathname)) return "/";
  return safeReturnPath(`${where.pathname}${where.search}${where.hash}`);
}

/** Paths that are not pages: the gateway, the console API and the edge answer them. */
const NON_PAGE_PREFIXES = ["/v1/", "/auth/", "/admin/api/", "/actuator/", "/_status/"];

/**
 * ALL-5774: where to come back to after a sign-in that starts on /error. A browser keeps
 * the original Referer across the edge's redirect, so for a link clicked on an Alloy page
 * (the signer app download on /get-app/android, say) this is that page. Another origin,
 * an API path, the sign-in and error pages themselves, or no referrer at all give "/".
 */
export function referrerReturnPath(
  referrer: string = typeof document !== "undefined" ? document.referrer : "",
  origin: string | undefined = typeof window !== "undefined" ? window.location?.origin : undefined,
): string {
  if (!referrer || !origin || origin === "null") return "/";
  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return "/";
  }
  if (url.origin !== origin) return "/";
  if (NON_PAGE_PREFIXES.some((prefix) => url.pathname.startsWith(prefix))) return "/";
  if (["/login", "/mfa", "/session-expired", "/error"].includes(url.pathname)) return "/";
  return safeReturnPath(`${url.pathname}${url.search}${url.hash}`);
}

/** The sign-in page URL that carries `returnTo`. */
export function signInPathFor(returnTo: string): string {
  const safe = safeReturnPath(returnTo);
  return safe === "/" ? "/login" : `/login?return_to=${encodeURIComponent(safe)}`;
}

/**
 * Asks the gateway whether the browser session is still authenticated.
 * Resolves true only on a definite "not authenticated" answer.
 */
export async function probeSessionEnded(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  let response: Response;
  try {
    response = await fetchImpl(SESSION_PROBE_PATH, {
      method: "GET",
      cache: "no-store" as RequestCache,
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
  } catch {
    return false;
  }
  if (response.status === 401) return true;
  if (response.status < 200 || response.status >= 300) return false;
  try {
    const body = (await response.json()) as { authenticated?: unknown } | null;
    return !(body && body.authenticated === true);
  } catch {
    return false;
  }
}

function storage(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** True when an automatic redirect is allowed now; records the time when it is. */
export function takeAutoRedirect(now: number): boolean {
  const store = storage();
  if (!store) return true;
  let last = Number.NaN;
  try {
    last = Number(store.getItem(SESSION_ENDED_GUARD_KEY));
  } catch {
    last = Number.NaN;
  }
  if (Number.isFinite(last) && last > 0 && now - last >= 0 && now - last < SESSION_ENDED_LOOP_GUARD_MS) {
    return false;
  }
  try {
    store.setItem(SESSION_ENDED_GUARD_KEY, String(now));
  } catch {
    // Blocked storage disables the guard's memory, never the redirect.
  }
  return true;
}

/**
 * Called by the API client for every response. Resolves true when this call
 * ended (or had already ended) the session.
 */
export function reportUnauthorized(path: string, status: number): Promise<boolean> {
  if (status !== 401 || isSessionProbeExempt(path)) return Promise.resolve(false);
  const active = config;
  if (!active || !active.isArmed()) return Promise.resolve(false);
  if (ended) return Promise.resolve(true);
  if (inFlight) return inFlight;
  inFlight = probeSessionEnded(active.fetchImpl ?? fetch)
    .then((isEnded) => {
      if (!isEnded || ended || config !== active || !active.isArmed()) return isEnded;
      ended = true;
      const now = (active.now ?? Date.now)();
      active.onEnded({
        returnTo: currentReturnPath(active.location?.()),
        autoRedirect: takeAutoRedirect(now),
      });
      return true;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
